import type { SampleCell } from '@/lib/stockpile/computeVolume';
import type { BestFitPlane } from '@/lib/stockpile/pointInPolygon';
import { atom } from 'jotai';

/**
 * Stockpile tool / compute panel state.
 *
 * The flow has two distinct stages:
 *   1. **Picking** — user clicks vertices on a point cloud surface inside the
 *      Three.js Canvas (driven by `<StockpileTool />`).
 *   2. **Computing / saving** — once the polygon is finalised (right-click or
 *      close-loop), `pickedPoints` is non-empty and `polygonClosed = true`,
 *      and `<StockpileComputePanel />` opens to drive the algorithm + persist
 *      the Feature.
 *
 * `sourceLayerId` is locked to the layer of the very first pick — subsequent
 * picks that hit a different PCO are rejected (see StockpileTool).
 */
export type StockpilePhase = 'idle' | 'picking' | 'closed';

export interface StockpileToolMode {
  enabled: boolean;
  phase: StockpilePhase;
  /** XYZ in project SRID — same shape as `addSurfaceModeAtom.pickedPoints`. */
  pickedPoints: Array<[number, number, number]>;
  /** Locks subsequent picks to a single PCO layer. Set on first pick. */
  sourceLayerId: string | null;
  /**
   * When set, the panel re-loads the existing feature's parameters into the
   * form on open, and Save calls `updateFeature` instead of `createFeature`.
   * Cleared by `DEFAULT_STOCKPILE_TOOL_MODE` (and therefore by Discard / Save /
   * Cancel). Set by clicking a saved-stockpile label in the 3D scene or by
   * the "Re-compute" action in `<FeatureProperties>`.
   */
  editFeatureId: string | null;
}

export const DEFAULT_STOCKPILE_TOOL_MODE: StockpileToolMode = {
  enabled: false,
  phase: 'idle',
  pickedPoints: [],
  sourceLayerId: null,
  editFeatureId: null,
};

export const stockpileToolModeAtom = atom<StockpileToolMode>(DEFAULT_STOCKPILE_TOOL_MODE);

/**
 * Write-only helper: enter edit mode on an existing stockpile feature. Reads
 * the feature's polygon ring + sourceLayerId from properties and seeds the
 * tool atom so `<StockpileComputePanel>` opens pre-populated.
 *
 * Called from two places:
 *   1. Clicking a saved stockpile's volume label in the 3D scene
 *      (`<SavedStockpiles>`).
 *   2. The "Re-compute" button in `<FeatureProperties>` when a STOCKPILE
 *      feature is selected.
 *
 * The feature must have:
 *   - `geometry.type === 'Polygon'` with at least one ring of ≥3 vertices.
 *   - `properties.sourceLayerId` (set during the original create flow).
 *
 * If either is missing, this no-ops — the upstream UI gates calling this on
 * `properties.kind === 'STOCKPILE'`, which guarantees the shape.
 */
export const enterStockpileEditModeAtom = atom(
  null,
  (
    _get,
    set,
    payload: {
      featureId: string;
      polygonRing: Array<[number, number, number]>;
      sourceLayerId: string;
    },
  ) => {
    if (payload.polygonRing.length < 3) return;
    set(stockpileToolModeAtom, {
      enabled: true,
      phase: 'closed',
      pickedPoints: payload.polygonRing,
      sourceLayerId: payload.sourceLayerId,
      editFeatureId: payload.featureId,
    });
  },
);

/**
 * Compute progress state — written by `computeStockpileVolume`'s `onProgress`
 * callback, read by the panel's progress bar. `null` when no compute is in
 * flight (idle or finished).
 *
 * `phase` distinguishes the two distinct cost centres: fetching octree node
 * data (network + worker decode, dominates wall time when the user hasn't
 * been over the polygon yet so the cache is cold) vs. iterating points to
 * produce the grid (CPU-bound, dominates once the data is resident). The
 * panel uses it to show an honest "Loading data…" / "Computing…" label
 * and to weight the two phases on a single 0–100 % bar.
 */
export interface StockpileComputeProgress {
  phase: 'loading' | 'computing';
  done: number;
  total: number;
}

export const stockpileComputeProgressAtom = atom<StockpileComputeProgress | null>(null);

/**
 * In-canvas visualization payload. Set by the compute panel after a successful
 * `computeStockpileVolume` run; consumed by `<StockpileVisualization />` to
 * render the base-plane mesh and per-cell height columns.
 *
 * Cleared when the user cancels, discards, saves, or starts a fresh polygon —
 * i.e. the lifetime is "result review window only". We deliberately don't
 * persist this with the saved Feature; rendering 40 000 height columns for
 * every saved stockpile every frame would be wasteful for a debug overlay.
 */
export interface StockpileVisualization {
  cells: SampleCell[];
  /** Polygon footprint in project SRID (XYZ) — used to triangulate the base-plane mesh. */
  polygonRing: Array<[number, number, number]>;
  basePlane: BestFitPlane;
  /** Used to normalise per-cell heights for the column colour ramp. */
  maxHeight: number;
}

export const stockpileVisualizationAtom = atom<StockpileVisualization | null>(null);

/** User-controlled toggle for showing/hiding the visualization while the panel is open. */
export const stockpileVisualizationVisibleAtom = atom<boolean>(true);
